import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Loader2, X, BookOpen, Image, Save } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';

interface StudentStory {
  id: number;
  name: string;
  jobRole?: string;
  storyText: string;
  quote?: string;
  photoUrl?: string;
  illustrationUrl?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

const emptyForm: Omit<StudentStory, 'id' | 'createdAt' | 'updatedAt'> = {
  name: '',
  jobRole: '',
  storyText: '',
  quote: '',
  photoUrl: '',
  illustrationUrl: '',
  isActive: true,
  sortOrder: 0,
};

const AdminStudentStories: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<StudentStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const { can } = usePermissions();
  const canWriteCms = can(PERMISSIONS.CMS_WRITE);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/public/admin/stories');
      if (res.ok) {
        const data = await res.json();
        setItems(Array.isArray(data) ? data : data.stories || []);
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '獲取學員故事失敗');
      }
    } catch {
      setError('獲取學員故事失敗');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setModalOpen(true);
  };

  const openEdit = (item: StudentStory) => {
    setEditingId(item.id);
    setForm({
      name: item.name || '',
      jobRole: item.jobRole || '',
      storyText: item.storyText || '',
      quote: item.quote || '',
      photoUrl: item.photoUrl || '',
      illustrationUrl: item.illustrationUrl || '',
      isActive: item.isActive ?? true,
      sortOrder: item.sortOrder ?? 0,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm({ ...emptyForm });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.storyText.trim()) {
      setError('姓名和故事內容為必填');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    try {
      const payload = {
        name: form.name.trim(),
        jobRole: form.jobRole?.trim() || undefined,
        storyText: form.storyText.trim(),
        quote: form.quote?.trim() || undefined,
        photoUrl: form.photoUrl?.trim() || undefined,
        illustrationUrl: form.illustrationUrl?.trim() || undefined,
        isActive: form.isActive,
        sortOrder: Number(form.sortOrder) || 0,
      };

      const res = editingId
        ? await apiFetch(`/api/public/admin/stories/${editingId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
        : await apiFetch('/api/public/admin/stories', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });

      if (res.ok) {
        setMessage(editingId ? '已更新學員故事' : '已新增學員故事');
        closeModal();
        await fetchItems();
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '儲存失敗');
      }
    } catch {
      setError('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: StudentStory) => {
    if (!confirm(`確定要刪除「${item.name}」的學員故事嗎？此操作無法復原。`)) return;

    try {
      const res = await apiFetch(`/api/public/admin/stories/${item.id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        setMessage('已刪除學員故事');
        await fetchItems();
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '刪除失敗');
      }
    } catch {
      setError('刪除失敗');
    }
  };

  const updateForm = (field: keyof typeof form, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <AdminLayout currentPage="student-stories">
      <div>
        {(error || message) && (
          <div
            className={`mb-4 p-4 rounded-lg flex justify-between items-center ${
              error
                ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 text-red-700 dark:text-red-400'
                : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 text-green-700 dark:text-green-400'
            }`}
          >
            <span>{error || message}</span>
            <button
              onClick={() => {
                setError('');
                setMessage('');
              }}
              className="hover:opacity-70"
            >
              ✕
            </button>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <BookOpen className="text-brand-600" size={22} />
              學員故事管理
            </h2>
            {canWriteCms && (
              <button
                onClick={openAdd}
                className="inline-flex items-center gap-2 px-4 py-2 text-white font-bold bg-brand-600 hover:bg-brand-700 rounded-lg shadow-sm transition-colors"
              >
                <Plus size={18} />
                新增故事
              </button>
            )}
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <Loader2 size={24} className="animate-spin mx-auto mb-2" />
              加載中...
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <BookOpen size={48} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              <p>尚未建立任何學員故事</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300">
                  <tr>
                    <th className="px-4 py-3 text-sm font-semibold">排序</th>
                    <th className="px-4 py-3 text-sm font-semibold">姓名</th>
                    <th className="px-4 py-3 text-sm font-semibold">職位</th>
                    <th className="px-4 py-3 text-sm font-semibold">故事摘要</th>
                    <th className="px-4 py-3 text-sm font-semibold">狀態</th>
                    <th className="px-4 py-3 text-sm font-semibold text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {item.sortOrder}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {item.photoUrl ? (
                            <img
                              src={item.photoUrl}
                              alt={item.name}
                              className="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-gray-600"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400 dark:text-gray-500">
                              <Image size={16} />
                            </div>
                          )}
                          <div className="font-medium text-gray-800 dark:text-white">{item.name}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {item.jobRole || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-xs">
                        <p className="truncate">{item.storyText}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            item.isActive
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {item.isActive ? '啟用' : '停用'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canWriteCms ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openEdit(item)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-brand-700 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20 rounded hover:bg-brand-100 dark:hover:bg-brand-900/30 transition-colors"
                            >
                              <Pencil size={14} />
                              編輯
                            </button>
                            <button
                              onClick={() => handleDelete(item)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                            >
                              <Trash2 size={14} />
                              刪除
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">
                  {editingId ? '編輯學員故事' : '新增學員故事'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      姓名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => updateForm('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      職位
                    </label>
                    <input
                      type="text"
                      value={form.jobRole}
                      onChange={(e) => updateForm('jobRole', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    故事內容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={form.storyText}
                    onChange={(e) => updateForm('storyText', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    語錄
                  </label>
                  <textarea
                    value={form.quote}
                    onChange={(e) => updateForm('quote', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none resize-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      照片 URL
                    </label>
                    <input
                      type="url"
                      value={form.photoUrl}
                      onChange={(e) => updateForm('photoUrl', e.target.value)}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      插圖 URL
                    </label>
                    <input
                      type="url"
                      value={form.illustrationUrl}
                      onChange={(e) => updateForm('illustrationUrl', e.target.value)}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      排序
                    </label>
                    <input
                      type="number"
                      value={form.sortOrder}
                      onChange={(e) => updateForm('sortOrder', Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 outline-none"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.isActive}
                        onChange={(e) => updateForm('isActive', e.target.checked)}
                        className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">啟用</span>
                    </label>
                  </div>
                </div>

                <div className="pt-4 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    取消
                  </button>
                  {canWriteCms && (
                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex items-center gap-2 px-6 py-2.5 text-white font-bold bg-brand-600 hover:bg-brand-700 rounded-lg shadow-sm disabled:opacity-60 transition-colors"
                    >
                      {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                      {saving ? '儲存中...' : '儲存'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminStudentStories;
